import { cn } from "@/utils/cn";
import { useSimulatedProgress } from "@/hooks/useSimulatedProgress";
import { LoadingProgressBar } from "@/components/ui/LoadingProgressBar";

export type LoaderSize  = "xs" | "sm" | "md" | "lg";
export type LoaderColor = "brand" | "white" | "slate";

interface LoaderProps {
  size?:  LoaderSize;
  color?: LoaderColor;
  className?: string;
  label?: string;
  showProgress?: boolean;
}

const sizeMap: Record<LoaderSize, string>  = {
  xs: "w-3 h-3 border-[1.5px]",
  sm: "w-4 h-4 border-2",
  md: "w-6 h-6 border-2",
  lg: "w-8 h-8 border-[3px]",
};

const colorMap: Record<LoaderColor, string> = {
  brand: "border-brand-200 border-t-brand-600",
  white: "border-white/30 border-t-white",
  slate: "border-slate-200 border-t-slate-500",
};

const valueColorMap: Record<LoaderColor, string> = {
  brand: "text-brand-700",
  white: "text-white",
  slate: "text-slate-500",
};

export function Loader({
  size = "md",
  color = "brand",
  className,
  label,
  showProgress = true,
}: LoaderProps) {
  const percent = useSimulatedProgress(showProgress);
  const compact = size === "xs" || size === "sm";

  return (
    <div
      className={cn(
        "flex items-center",
        compact ? "flex-row gap-1.5" : "flex-col gap-2",
        className,
      )}
      role="status"
      aria-label={label ?? "Carregando"}
      aria-busy="true"
    >
      <div
        className={cn(
          "flex-shrink-0 rounded-full animate-spin",
          sizeMap[size],
          colorMap[color],
        )}
      />
      {showProgress && compact && (
        <span className={cn("text-[10px] font-bold tabular-nums leading-none", valueColorMap[color])}>
          {percent}%
        </span>
      )}
      {!compact && (
        <div className={cn("flex flex-col items-center gap-2", showProgress && "w-40")}>
          {label && (
            <span
              className={cn(
                "text-xs font-medium text-center",
                color === "white" ? "text-white/80" : "text-slate-500",
              )}
            >
              {label}
            </span>
          )}
          {showProgress && (
            <LoadingProgressBar
              percent={percent}
              size={size === "lg" ? "md" : "sm"}
              color={color === "white" ? "white" : "brand"}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function PageLoader({ label = "Carregando..." }: { label?: string }) {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[300px]">
      <Loader size="lg" label={label} />
    </div>
  );
}
